import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DESCRICAO_DO_PAPEL,
  PAPEIS,
  type OperadorDaPlataforma,
  type Papel,
  type SituacaoOperador,
} from '@aponte/shared';
import { api } from '../api/cliente.js';
import { Aviso, mensagemDoErro, type Mensagem } from '../componentes/Aviso.js';
import { Dialogo } from '../componentes/Dialogo.js';
import { useSessao } from '../autenticacao/ContextoDeSessao.js';

/** O que a situação quer dizer, e o que o admin deve fazer a respeito. */
const SITUACAO: Record<SituacaoOperador, { rotulo: string; tom: string; explica: string }> = {
  ativo: { rotulo: 'Ativo', tom: 'positivo', explica: 'Em uso.' },
  convidado: {
    rotulo: 'Convite enviado',
    tom: 'atencao',
    explica: 'Aguardando o primeiro acesso. A senha temporária ainda vale.',
  },
  expirado: {
    rotulo: 'Convite vencido',
    tom: 'negativo',
    explica:
      'A senha temporária venceu antes do primeiro acesso. Esta pessoa não consegue entrar ' +
      'nem recuperar a senha — reenvie o convite.',
  },
  desativado: {
    rotulo: 'Desativado',
    tom: 'negativo',
    explica: 'Bloqueado, sem conseguir entrar.',
  },
};

type Pergunta =
  | { tipo: 'situacao'; operador: OperadorDaPlataforma; ativar: boolean }
  | { tipo: 'mfa'; operador: OperadorDaPlataforma }
  | { tipo: 'convite'; operador: OperadorDaPlataforma };

/**
 * Gestão dos operadores da plataforma.
 *
 * Só `admin` chega aqui. As ações destrutivas conversam com o Cognito, não com a nossa
 * base: quem some daqui perde o acesso ao cockpit inteiro.
 *
 * Não existe "excluir": a trilha de auditoria aponta para quem emitiu cada documento e
 * gerou cada cobrança, e apagar a conta não apagaria o passado — só o deixaria sem dono.
 * Desativar bloqueia o acesso e preserva a resposta de "quem fez isto?".
 */
export function Operadores() {
  const consultas = useQueryClient();
  const navegar = useNavigate();
  const { operador: eu, temPapel } = useSessao();

  const [mensagem, setMensagem] = useState<Mensagem | null>(null);
  const [pergunta, setPergunta] = useState<Pergunta | null>(null);
  const [criando, setCriando] = useState(false);

  const consulta = useQuery({ queryKey: ['operadores'], queryFn: api.listarOperadores });

  const recarregar = () => void consultas.invalidateQueries({ queryKey: ['operadores'] });
  const aoFalhar = (erro: unknown) => setMensagem({ tom: 'erro', texto: mensagemDoErro(erro) });

  const papeis = useMutation({
    mutationFn: ({ id, papeis: p }: { id: string; papeis: Papel[] }) => api.definirPapeis(id, p),
    onSuccess: (o) => {
      setMensagem({ tom: 'ok', texto: `Papéis de ${o.nome} atualizados.` });
      recarregar();
    },
    onError: aoFalhar,
  });

  const situacao = useMutation({
    mutationFn: ({ id, ativo, motivo }: { id: string; ativo: boolean; motivo: string }) =>
      api.definirSituacao(id, ativo, motivo),
    onSuccess: (o) => {
      setMensagem({
        tom: 'ok',
        texto: `${o.nome} foi ${o.situacao === 'desativado' ? 'desativado' : 'reativado'}.`,
      });
      recarregar();
    },
    onError: aoFalhar,
  });

  const convite = useMutation({
    mutationFn: (id: string) => api.reenviarConvite(id),
    onSuccess: (o) => {
      setMensagem({
        tom: 'ok',
        texto: `Convite reenviado para ${o.email}. A senha temporária anterior deixou de valer.`,
      });
      recarregar();
    },
    onError: aoFalhar,
  });

  const mfa = useMutation({
    mutationFn: (id: string) => api.reiniciarMfa(id),
    onSuccess: (o) => {
      setMensagem({
        tom: 'ok',
        texto: `Segundo fator de ${o.nome} reiniciado. No próximo acesso o aplicativo autenticador será cadastrado de novo.`,
      });
      recarregar();
    },
    onError: aoFalhar,
  });

  if (!temPapel('admin')) {
    return (
      <div className="aviso erro" style={{ marginTop: 24 }}>
        Esta área é restrita a administradores.
      </div>
    );
  }

  const operadores = consulta.data ?? [];
  const ocupado =
    papeis.isPending || situacao.isPending || convite.isPending || mfa.isPending || criando;

  return (
    <>
      <div className="cabecalho-secao" style={{ marginTop: 24 }}>
        <div>
          <h2>
            Operadores da <b>plataforma</b>
          </h2>
          <div className="dica">
            Quem entra no cockpit, com qual papel. O acesso é nominal: cada documento emitido e cada
            cobrança gerada ficam registrados no nome de quem os fez.
          </div>
        </div>
        <button className="bt" onClick={() => setCriando(true)} disabled={ocupado}>
          + Novo operador
        </button>
      </div>

      {mensagem && <Aviso mensagem={mensagem} />}

      {consulta.isLoading && <div className="dica">Carregando…</div>}
      {consulta.isError && (
        <div className="aviso erro">Não foi possível carregar os operadores.</div>
      )}

      <table className="funil">
        <thead>
          <tr>
            <th>Pessoa</th>
            <th>Papéis</th>
            <th>Situação</th>
            <th>Segundo fator</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {operadores.map((o) => {
            const souEu = o.id === eu?.id;
            const situacaoDele = SITUACAO[o.situacao];

            return (
              <tr key={o.id}>
                <td>
                  <b>{o.nome || '(sem nome)'}</b>
                  {souEu && <span className="selo-eu"> você</span>}
                  <div className="dica">{o.email}</div>
                </td>

                <td>
                  {/*
                   * Alterar os próprios papéis fica travado no servidor também: um admin
                   * que se rebaixa perde justamente a tela que usaria para desfazer.
                   */}
                  <div className="papeis">
                    {PAPEIS.map((papel) => (
                      <label key={papel} title={DESCRICAO_DO_PAPEL[papel]}>
                        <input
                          type="checkbox"
                          checked={o.papeis.includes(papel)}
                          disabled={souEu || ocupado || o.situacao === 'desativado'}
                          onChange={(e) => {
                            const novos = e.target.checked
                              ? [...o.papeis, papel]
                              : o.papeis.filter((p) => p !== papel);
                            if (novos.length === 0) {
                              setMensagem({
                                tom: 'erro',
                                texto: 'Um operador precisa de ao menos um papel.',
                              });
                              return;
                            }
                            papeis.mutate({ id: o.id, papeis: novos });
                          }}
                        />
                        {papel}
                      </label>
                    ))}
                  </div>
                </td>

                <td>
                  <span className={`selo ${situacaoDele.tom}`}>{situacaoDele.rotulo}</span>
                  <div className="dica">{situacaoDele.explica}</div>
                </td>

                <td>
                  {o.mfaCadastrado ? (
                    <>
                      <span className="dica">Cadastrado</span>
                      <div>
                        <button
                          className="bt-texto"
                          style={{ width: 'auto', margin: 0 }}
                          disabled={ocupado || o.situacao === 'desativado'}
                          onClick={() => setPergunta({ tipo: 'mfa', operador: o })}
                        >
                          Reiniciar
                        </button>
                      </div>
                    </>
                  ) : (
                    <span className="dica">Pendente</span>
                  )}
                </td>

                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {(o.situacao === 'convidado' || o.situacao === 'expirado') && (
                    <button
                      className="bt vazado pequeno"
                      disabled={ocupado}
                      onClick={() => setPergunta({ tipo: 'convite', operador: o })}
                    >
                      Reenviar convite
                    </button>
                  )}{' '}
                  {!souEu && (
                    <button
                      className={`bt pequeno ${o.situacao === 'desativado' ? 'vazado' : 'perigo'}`}
                      disabled={ocupado}
                      onClick={() =>
                        setPergunta({
                          tipo: 'situacao',
                          operador: o,
                          ativar: o.situacao === 'desativado',
                        })
                      }
                    >
                      {o.situacao === 'desativado' ? 'Reativar' : 'Desativar'}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {operadores.length === 0 && !consulta.isLoading && (
        <div className="dica" style={{ marginTop: 12 }}>
          Nenhum operador cadastrado.
        </div>
      )}

      <button className="bt-texto" onClick={() => navegar('/')} style={{ marginTop: 24 }}>
        Voltar ao painel
      </button>

      {criando && (
        <NovoOperador
          aoFechar={() => setCriando(false)}
          aoCriar={(o) => {
            setCriando(false);
            setMensagem({
              tom: 'ok',
              texto: `${o.nome} criado. O convite com a senha temporária foi enviado para ${o.email}.`,
            });
            recarregar();
          }}
          aoFalhar={aoFalhar}
        />
      )}

      {pergunta?.tipo === 'situacao' && (
        <Dialogo
          titulo={`${pergunta.ativar ? 'Reativar' : 'Desativar'} ${pergunta.operador.nome}`}
          campo={{
            rotulo: 'Motivo',
            obrigatorio: true,
            minimo: 10,
            placeholder: pergunta.ativar ? 'Ex.: voltou de licença' : 'Ex.: saiu da equipe',
          }}
          rotuloConfirmar={pergunta.ativar ? 'Reativar' : 'Desativar'}
          tomConfirmar={pergunta.ativar ? 'normal' : 'perigo'}
          ocupado={situacao.isPending}
          aoCancelar={() => setPergunta(null)}
          aoConfirmar={(motivo) => {
            situacao.mutate({ id: pergunta.operador.id, ativo: pergunta.ativar, motivo });
            setPergunta(null);
          }}
        >
          {pergunta.ativar ? (
            <p>A pessoa volta a entrar com a mesma senha e o mesmo aplicativo autenticador.</p>
          ) : (
            <p>
              O acesso é bloqueado imediatamente. O histórico de documentos e cobranças continua no
              nome dela — desativar não apaga nada.
            </p>
          )}
        </Dialogo>
      )}

      {pergunta?.tipo === 'convite' && (
        <Dialogo
          titulo={`Reenviar convite para ${pergunta.operador.nome}`}
          rotuloConfirmar="Reenviar"
          ocupado={convite.isPending}
          aoCancelar={() => setPergunta(null)}
          aoConfirmar={() => {
            convite.mutate(pergunta.operador.id);
            setPergunta(null);
          }}
        >
          <p>
            Uma senha temporária nova vai para <b>{pergunta.operador.email}</b>, e a anterior deixa
            de valer.
          </p>
        </Dialogo>
      )}

      {pergunta?.tipo === 'mfa' && (
        <Dialogo
          titulo={`Reiniciar o segundo fator de ${pergunta.operador.nome}`}
          rotuloConfirmar="Reiniciar"
          tomConfirmar="perigo"
          ocupado={mfa.isPending}
          aoCancelar={() => setPergunta(null)}
          aoConfirmar={() => {
            mfa.mutate(pergunta.operador.id);
            setPergunta(null);
          }}
        >
          <p>
            Use quando a pessoa perder o celular com o aplicativo autenticador. O cadastro atual é
            descartado e o próximo acesso pedirá um novo — a senha continua a mesma.
          </p>
          <p className="dica">
            Confirme por outro meio que é ela mesma quem está pedindo. Este é o passo que derruba o
            segundo fator de quem entra no cockpit.
          </p>
        </Dialogo>
      )}
    </>
  );
}

/** Formulário de criação. O convite com a senha temporária sai no momento do cadastro. */
function NovoOperador({
  aoFechar,
  aoCriar,
  aoFalhar,
}: {
  aoFechar: () => void;
  aoCriar: (operador: OperadorDaPlataforma) => void;
  aoFalhar: (erro: unknown) => void;
}) {
  const [email, setEmail] = useState('');
  const [nome, setNome] = useState('');
  const [escolhidos, setEscolhidos] = useState<Papel[]>(['closer']);
  const [erro, setErro] = useState('');

  const criar = useMutation({
    mutationFn: () =>
      api.criarOperador({ email: email.trim(), nome: nome.trim(), papeis: escolhidos }),
    onSuccess: aoCriar,
    onError: (e) => {
      setErro(mensagemDoErro(e));
      aoFalhar(e);
    },
  });

  return (
    <div
      className="fundo-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Novo operador"
      onClick={(e) => {
        if (e.target === e.currentTarget) aoFechar();
      }}
    >
      <div className="modal">
        <h3>Novo operador</h3>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setErro('');
            if (escolhidos.length === 0) {
              setErro('Escolha ao menos um papel.');
              return;
            }
            criar.mutate();
          }}
        >
          <div className="campo" style={{ marginTop: 14 }}>
            <label htmlFor="novo-nome">Nome completo</label>
            <input
              id="novo-nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Como deve aparecer na trilha de auditoria"
              autoFocus
              required
            />
          </div>

          <div className="campo">
            <label htmlFor="novo-email">E-mail</label>
            <input
              id="novo-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="nome@rrevela.com.br"
              required
            />
            <div className="dica">
              O convite com a senha temporária vai para este endereço, e ele não pode ser alterado
              depois.
            </div>
          </div>

          <div className="campo">
            <label>Papéis</label>
            <div className="papeis coluna">
              {PAPEIS.map((papel) => (
                <label key={papel}>
                  <input
                    type="checkbox"
                    checked={escolhidos.includes(papel)}
                    onChange={(e) =>
                      setEscolhidos((atuais) =>
                        e.target.checked ? [...atuais, papel] : atuais.filter((p) => p !== papel),
                      )
                    }
                  />
                  <span>
                    <b>{papel}</b> — {DESCRICAO_DO_PAPEL[papel]}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {erro && (
            <div className="aviso erro" style={{ marginTop: 12 }}>
              {erro}
            </div>
          )}

          <div className="modal-acoes">
            <button
              type="button"
              className="bt vazado pequeno"
              onClick={aoFechar}
              disabled={criar.isPending}
            >
              Cancelar
            </button>
            <button className="bt pequeno" type="submit" disabled={criar.isPending}>
              {criar.isPending ? 'Criando…' : 'Criar e enviar convite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
